import { del, get, post, put } from "aws-amplify/api";

/**
 * Sales deposits (uang muka penjualan) on the accounting API.
 *
 * Plain promise helpers rather than reducer actions: the deposit screens each
 * hold their own copy of what they show, and the shared accounting slice
 * (`request`, `submitError`) is already contended by the invoice and payment
 * forms these screens sit on top of.
 *
 * Every helper resolves to the envelope's `data` and throws the raw API error
 * otherwise, so callers can hand it to getErrorMessages / getPeriodClosedError
 * / getDepositErrorMessage untouched. A 2xx envelope with `success: false` is
 * rethrown in the same shape.
 */

const API_NAME = "accounting";

const unwrap = async (request) => {
  const { body } = await request.response;
  const res = await body.json();
  if (res?.success === false) {
    // Same shape an amplify HTTP error carries, so one parser reads both.
    const error = new Error(res.message || "Request failed");
    error.response = { body: res };
    throw error;
  }
  return res?.data;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, value);
    }
  });
  return search.toString();
};

/**
 * `status` is "open" (Belum dipakai + Sebagian) or one status code.
 * Resolves to `{ items, summary: { remaining, count } }`.
 */
export const listDeposits = async ({ businessID, partnerID, status }) => {
  const data = await unwrap(
    get({
      apiName: API_NAME,
      path: `/deposit?${query({
        business_id: businessID,
        partner_id: partnerID,
        status,
      })}`,
    }),
  );
  return {
    items: data?.items || [],
    summary: data?.summary || { remaining: 0, count: 0 },
  };
};

export const getDeposit = (id, businessID) =>
  unwrap(
    get({
      apiName: API_NAME,
      path: `/deposit/${encodeURIComponent(id)}?${query({
        business_id: businessID,
      })}`,
    }),
  );

export const createDeposit = (body) =>
  unwrap(post({ apiName: API_NAME, path: "/deposit", options: { body } }));

export const updateDeposit = (body) =>
  unwrap(put({ apiName: API_NAME, path: "/deposit", options: { body } }));

export const deleteDeposit = (id) =>
  unwrap(
    del({ apiName: API_NAME, path: `/deposit/${encodeURIComponent(id)}` }),
  );

/** `amount` comes off the invoice's Sisa Pembayaran. Resolves to `{ usageId, date }`. */
export const applyDeposit = (id, { invoiceID, amount }) =>
  unwrap(
    post({
      apiName: API_NAME,
      path: `/deposit/${encodeURIComponent(id)}/apply`,
      options: { body: { invoice_id: invoiceID, amount } },
    }),
  );

export const refundDeposit = (id, body) =>
  unwrap(
    post({
      apiName: API_NAME,
      path: `/deposit/${encodeURIComponent(id)}/refund`,
      options: { body },
    }),
  );

export const forfeitDeposit = (id, body) =>
  unwrap(
    post({
      apiName: API_NAME,
      path: `/deposit/${encodeURIComponent(id)}/forfeit`,
      options: { body },
    }),
  );

/** Undoes one application, refund or forfeit. */
export const cancelDepositUsage = (usageID) =>
  unwrap(
    del({
      apiName: API_NAME,
      path: `/deposit/usage/${encodeURIComponent(usageID)}`,
    }),
  );
